import { Box, SxProps, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from "@mui/material";
import { useDrillDownDialog } from "../DrillDownProvider";
import { DrillDownConfig, WidgetExplanationContent } from "../types";
import { WidgetCard } from "./WidgetCard";
import { dashboardTableSx, ROW_HOVER_BACKGROUND, ZEBRA_ROW_BACKGROUND } from "./tableStyles";

export type DataTableColumn = {
    key: string;
    label: string;
    isNumeric?: boolean;
    width?: number;
};

type DataTableCardProps = {
    title: string;
    explanation: WidgetExplanationContent;
    columns: Array<DataTableColumn>;
    rows: Array<Record<string, React.ReactNode>>;
    totalsRow?: Record<string, React.ReactNode>;
    maxBodyHeight?: number;
    headerControls?: React.ReactNode;
    isLoading?: boolean;
    isError?: boolean;
    getRowSx?: (rowIndex: number) => SxProps | undefined;
    rowDrillDown?: (rowIndex: number) => DrillDownConfig | null;
};

export const DataTableCard = ({
    title,
    explanation,
    columns,
    rows,
    totalsRow,
    maxBodyHeight,
    headerControls,
    isLoading,
    isError,
    getRowSx,
    rowDrillDown,
}: DataTableCardProps) => {
    const drillDownDialog = useDrillDownDialog();

    const openRowDrillDown = (rowIndex: number) => {
        const config = rowDrillDown ? rowDrillDown(rowIndex) : null;
        if (config && drillDownDialog) drillDownDialog.openDrillDown(config);
    };

    return (
        <WidgetCard title={title} explanation={explanation} bodyHeight={160} bodyAlign="top" isLoading={isLoading} isError={isError} headerControls={headerControls}>
            <Box width="100%">
                <TableContainer sx={{ maxHeight: maxBodyHeight }}>
                    <Table size="small" stickyHeader sx={dashboardTableSx}>
                        <TableHead>
                            <TableRow>
                                {columns.map((column) => (
                                    <TableCell key={column.key} align={column.isNumeric ? "right" : "left"} sx={{ fontWeight: 600, width: column.width }}>
                                        {column.label}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rows.map((row, rowIndex) => {
                                const isClickable = Boolean(drillDownDialog && rowDrillDown && rowDrillDown(rowIndex));
                                return (
                                    <TableRow
                                        key={rowIndex}
                                        onClick={isClickable ? () => openRowDrillDown(rowIndex) : undefined}
                                        sx={{
                                            ...(rowIndex % 2 === 1 ? { backgroundColor: ZEBRA_ROW_BACKGROUND } : {}),
                                            ...(isClickable ? { cursor: "pointer" } : {}),
                                            ...(getRowSx?.(rowIndex) || {}),
                                            "&:hover": { backgroundColor: ROW_HOVER_BACKGROUND },
                                        }}
                                    >
                                        {columns.map((column) => (
                                            <TableCell key={column.key} align={column.isNumeric ? "right" : "left"}>
                                                {row[column.key] ?? "—"}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                );
                            })}
                            {totalsRow && (
                                <TableRow>
                                    {columns.map((column) => (
                                        <TableCell
                                            key={column.key}
                                            align={column.isNumeric ? "right" : "left"}
                                            sx={{ fontWeight: 700, borderTop: "2px solid #d6d9de" }}
                                        >
                                            {totalsRow[column.key] ?? ""}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>
        </WidgetCard>
    );
};
